import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Card,
  CardBody,
  Icon,
  Badge,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
  Select,
  Progress,
  Circle,
} from '@chakra-ui/react';
import {
  ArrowLeft,
  Buildings,
  CurrencyDollar,
  Receipt,
  TrendUp,
  Users,
  CalendarBlank,
  FileText,
  Percent,
  UserPlus,
  UserMinus,
  Sun,
  FirstAidKit,
  MapPin,
  IdentificationCard,
} from 'phosphor-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import { toast } from 'sonner';
import { api } from '../../../services/api';
import SecaoClienteLayout from '../layout';
import { formatCNPJ } from '../../../utils/FormatFunctions';

interface ISocio {
  nome_socio: string;
  inscricao: string;
  participacao: number;
  qt_cotas: number;
  data_ingresso: string;
}

interface IEmpresa {
  id: number;
  external_id: string;
  nome: string;
  cnpj: string;
  razao_social: string;
  cidade: string;
  uf: string;
  dt_cli_ate: string | null;
  dt_cli_desde: string | null;
  ativa: boolean;
  regime_tributario_atualizado: string;
  socios: ISocio[];
}

interface IFaturamentoMes {
  mes: number;
  mes_nome: string;
  faturamento: {
    servico: number;
    saida: number;
    outros: number;
    total: number;
  };
  impostos: {
    simples_nacional?: number;
    pis?: number;
    cofins?: number;
    icms?: number;
    csll?: number;
    irpj?: number;
    iss?: number;
  };
}

interface IFolhaMes {
  mes: number;
  mes_nome: string;
  admissoes: number;
  demissoes: number;
  ferias: number;
  afastamentos: number;
  fgts: number;
  dctfweb: number;
}

interface INotasMes {
  mes: number;
  mes_nome: string;
  notas: {
    nfe: number;
    nfce: number;
    nfse: number;
    cte: number;
    total: number;
  };
  quebras: {
    nfe: number;
    nfce: number;
    nfse: number;
    cte: number;
    total: number;
  };
}

interface IDossieData {
  empresa: IEmpresa;
  faturamento_impostos: {
    ano: number;
    meses: IFaturamentoMes[];
    totais: any;
  };
  folha_pagamento: {
    ano: number;
    qnt_vidas: number;
    qnt_prolabore: number;
    meses: IFolhaMes[];
    totais: any;
  };
  notas_quebras: {
    ano: number;
    meses: INotasMes[];
    totais: any;
  };
}

interface ClienteData {
  id: number;
  guid: string;
  nome: string;
  email: string;
  empresas?: Array<{
    id: number;
    razao_social: string;
    nome_fantasia: string;
    cnpj: string | null;
    external_id?: string;
  }>;
}

const SecaoClienteDossieEmpresa = () => {
  const { empresaId } = useParams<{ empresaId: string }>();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [dossieData, setDossieData] = useState<IDossieData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [anoSelecionado, setAnoSelecionado] = useState(new Date().getFullYear());

  // Pegar dados do cliente do localStorage
  const clienteDataStr = localStorage.getItem('cliente_data');
  const clienteData: ClienteData | null = clienteDataStr ? JSON.parse(clienteDataStr) : null;

  // Formatar moeda
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value || 0);
  };

  const formatCurrencyCompact = (value: number) => {
    if (value >= 1000000) {
      return `R$ ${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `R$ ${(value / 1000).toFixed(1)}K`;
    }
    return formatCurrency(value);
  };

  // Buscar dados do dossiê
  useEffect(() => {
    const fetchDossie = async () => {
      if (!empresaId || !clienteData?.id) {
        setError('Dados inválidos');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem('cliente_access_token');
        const response = await api.get(`/api/v1/secao-cliente/dossie/${empresaId}/`, {
          params: {
            cliente_id: clienteData.id,
            ano: anoSelecionado,
          },
          headers: { Authorization: `Bearer ${token}` },
        });

        setDossieData(response.data.data);
      } catch (err: any) {
        const errorMessage = err?.response?.data?.detail || 'Erro ao carregar dossiê';
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDossie();
  }, [empresaId, clienteData?.id, anoSelecionado]);

  // Cálculos consolidados
  const totais = useMemo(() => {
    if (!dossieData?.faturamento_impostos?.meses) {
      return {
        faturamento: 0,
        impostos: 0,
        servicos: 0,
        saidas: 0,
      };
    }

    return dossieData.faturamento_impostos.meses.reduce(
      (acc, mes) => {
        const impostosMes = Object.values(mes.impostos || {}).reduce(
          (sum: number, val) => sum + (Number(val) || 0),
          0,
        );
        return {
          faturamento: acc.faturamento + (mes.faturamento?.total || 0),
          impostos: acc.impostos + impostosMes,
          servicos: acc.servicos + (mes.faturamento?.servico || 0),
          saidas: acc.saidas + (mes.faturamento?.saida || 0),
        };
      },
      { faturamento: 0, impostos: 0, servicos: 0, saidas: 0 },
    );
  }, [dossieData]);

  const totaisFolha = useMemo(() => {
    if (!dossieData?.folha_pagamento) {
      return {
        fgts: 0,
        dctfweb: 0,
        admissoes: 0,
        demissoes: 0,
        ferias: 0,
        afastamentos: 0,
        vidas: 0,
        prolabore: 0,
      };
    }

    const folha = dossieData.folha_pagamento;
    const meses = folha.meses || [];

    return {
      fgts: meses.reduce((sum, m) => sum + (m.fgts || 0), 0),
      dctfweb: meses.reduce((sum, m) => sum + (m.dctfweb || 0), 0),
      admissoes: meses.reduce((sum, m) => sum + (m.admissoes || 0), 0),
      demissoes: meses.reduce((sum, m) => sum + (m.demissoes || 0), 0),
      ferias: meses.reduce((sum, m) => sum + (m.ferias || 0), 0),
      afastamentos: meses.reduce((sum, m) => sum + (m.afastamentos || 0), 0),
      vidas: folha.qnt_vidas || 0,
      prolabore: folha.qnt_prolabore || 0,
    };
  }, [dossieData]);

  const totaisNotas = useMemo(() => {
    if (!dossieData?.notas_quebras?.meses) {
      return { notas: 0, quebras: 0 };
    }

    return dossieData.notas_quebras.meses.reduce(
      (acc, mes) => ({
        notas: acc.notas + (mes.notas?.total || 0),
        quebras: acc.quebras + (mes.quebras?.total || 0),
      }),
      { notas: 0, quebras: 0 },
    );
  }, [dossieData]);

  // Percentual de impostos
  const percentualImpostos =
    totais.faturamento > 0 ? (totais.impostos / totais.faturamento) * 100 : 0;

  // Dados para gráfico de faturamento
  const chartDataFaturamento = useMemo(() => {
    if (!dossieData?.faturamento_impostos?.meses) return [];
    return dossieData.faturamento_impostos.meses.map(mes => ({
      mes: mes.mes_nome?.substring(0, 3) || `M${mes.mes}`,
      faturamento: mes.faturamento?.total || 0,
      impostos: Object.values(mes.impostos || {}).reduce(
        (sum: number, val) => sum + (Number(val) || 0),
        0,
      ),
    }));
  }, [dossieData]);

  // Dados para gráfico de folha
  const chartDataFolha = useMemo(() => {
    if (!dossieData?.folha_pagamento?.meses) return [];
    return dossieData.folha_pagamento.meses.map(mes => ({
      mes: mes.mes_nome?.substring(0, 3) || `M${mes.mes}`,
      fgts: mes.fgts || 0,
      dctfweb: mes.dctfweb || 0,
    }));
  }, [dossieData]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box bg="white" p={3} borderRadius="md" boxShadow="lg" border="1px" borderColor="gray.200">
          <Text fontWeight="bold" mb={2}>
            {label}
          </Text>
          {payload.map((entry: any) => (
            <Text key={entry.name} fontSize="sm" color={entry.color}>
              {entry.name}: {formatCurrency(entry.value)}
            </Text>
          ))}
        </Box>
      );
    }
    return null;
  };

  if (isLoading) {
    return (
      <SecaoClienteLayout>
        <Flex justify="center" align="center" minH="400px">
          <VStack spacing={4}>
            <Spinner size="xl" color="blue.500" thickness="4px" />
            <Text color="gray.500">Carregando dossiê...</Text>
          </VStack>
        </Flex>
      </SecaoClienteLayout>
    );
  }

  if (error) {
    return (
      <SecaoClienteLayout>
        <VStack spacing={4}>
          <Alert status="error" borderRadius="lg">
            <AlertIcon />
            {error}
          </Alert>
          <Box
            as="button"
            onClick={() => navigate('/dossie')}
            color="blue.500"
            _hover={{ textDecoration: 'underline' }}
          >
            ← Voltar para lista de empresas
          </Box>
        </VStack>
      </SecaoClienteLayout>
    );
  }

  const empresa = dossieData?.empresa;

  return (
    <SecaoClienteLayout>
      <VStack spacing={6} align="stretch">
        {/* Header com informações da empresa */}
        <Card bg="linear-gradient(135deg, #1E3A5F 0%, #2D5A87 100%)" color="white">
          <CardBody>
            <Flex
              direction={{ base: 'column', md: 'row' }}
              justify="space-between"
              align={{ base: 'start', md: 'center' }}
              gap={4}
            >
              <HStack spacing={4}>
                <Box
                  p={2}
                  borderRadius="lg"
                  bg="whiteAlpha.200"
                  cursor="pointer"
                  _hover={{ bg: 'whiteAlpha.300' }}
                  onClick={() => navigate('/dossie')}
                >
                  <Icon as={ArrowLeft} boxSize={5} />
                </Box>
                <Box>
                  <Heading size="lg">{empresa?.razao_social || empresa?.nome}</Heading>
                  <HStack spacing={4} mt={2} flexWrap="wrap">
                    {empresa?.cnpj && (
                      <HStack spacing={1}>
                        <Icon as={IdentificationCard} />
                        <Text fontSize="sm" opacity={0.9}>
                          {formatCNPJ(empresa.cnpj)}
                        </Text>
                      </HStack>
                    )}
                    {empresa?.cidade && (
                      <HStack spacing={1}>
                        <Icon as={MapPin} />
                        <Text fontSize="sm" opacity={0.9}>
                          {empresa.cidade}/{empresa.uf}
                        </Text>
                      </HStack>
                    )}
                    {empresa?.regime_tributario_atualizado && (
                      <Badge colorScheme="green" variant="solid">
                        {empresa.regime_tributario_atualizado}
                      </Badge>
                    )}
                  </HStack>
                </Box>
              </HStack>

              {/* Seletor de ano */}
              <HStack>
                <Icon as={CalendarBlank} />
                <Select
                  value={anoSelecionado}
                  onChange={e => setAnoSelecionado(Number(e.target.value))}
                  bg="white"
                  color="gray.800"
                  size="sm"
                  w="100px"
                  borderRadius="lg"
                >
                  {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(ano => (
                    <option key={ano} value={ano}>
                      {ano}
                    </option>
                  ))}
                </Select>
              </HStack>
            </Flex>
          </CardBody>
        </Card>

        {/* KPI Cards Principais */}
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
          <Card>
            <CardBody>
              <Flex align="center" mb={3}>
                <Circle size="40px" bg="green.100">
                  <Icon as={CurrencyDollar} boxSize={5} color="green.600" />
                </Circle>
              </Flex>
              <Text fontSize="sm" color="gray.500" fontWeight="medium">
                Faturamento Total
              </Text>
              <Text fontSize="2xl" fontWeight="bold" color="gray.800">
                {formatCurrencyCompact(totais.faturamento)}
              </Text>
              <Text fontSize="xs" color="gray.400">
                {anoSelecionado}
              </Text>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <Flex align="center" mb={3}>
                <Circle size="40px" bg="red.100">
                  <Icon as={Receipt} boxSize={5} color="red.600" />
                </Circle>
              </Flex>
              <Text fontSize="sm" color="gray.500" fontWeight="medium">
                Total Impostos
              </Text>
              <Text fontSize="2xl" fontWeight="bold" color="gray.800">
                {formatCurrencyCompact(totais.impostos + totaisFolha.fgts + totaisFolha.dctfweb)}
              </Text>
              <Text fontSize="xs" color="gray.400">
                Fiscal + DP
              </Text>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <Flex align="center" mb={3}>
                <Circle size="40px" bg="purple.100">
                  <Icon as={Percent} boxSize={5} color="purple.600" />
                </Circle>
              </Flex>
              <Text fontSize="sm" color="gray.500" fontWeight="medium">
                Carga Tributária
              </Text>
              <Text fontSize="2xl" fontWeight="bold" color="gray.800">
                {percentualImpostos.toFixed(1)}%
              </Text>
              <Text fontSize="xs" color="gray.400">
                Impostos / Faturamento
              </Text>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <Flex align="center" mb={3}>
                <Circle size="40px" bg="blue.100">
                  <Icon as={Users} boxSize={5} color="blue.600" />
                </Circle>
              </Flex>
              <Text fontSize="sm" color="gray.500" fontWeight="medium">
                Colaboradores
              </Text>
              <Text fontSize="2xl" fontWeight="bold" color="gray.800">
                {totaisFolha.vidas}
              </Text>
              <HStack spacing={2} fontSize="xs">
                <Text color="green.500">+{totaisFolha.admissoes}</Text>
                <Text color="red.500">-{totaisFolha.demissoes}</Text>
              </HStack>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Seção Fiscal */}
        <Card overflow="hidden">
          <Box bg="orange.500" px={5} py={3}>
            <HStack>
              <Icon as={Buildings} boxSize={5} color="white" />
              <Text fontSize="lg" fontWeight="bold" color="white">
                Fiscal
              </Text>
            </HStack>
          </Box>
          <CardBody>
            {/* KPIs Fiscais */}
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
              <Box p={4} bg="orange.50" borderRadius="lg" textAlign="center">
                <Text fontSize="xs" color="gray.500" fontWeight="medium">
                  Total Impostos Fiscais
                </Text>
                <Text fontSize="xl" fontWeight="bold" color="orange.600">
                  {formatCurrencyCompact(totais.impostos)}
                </Text>
              </Box>
              <Box p={4} bg="green.50" borderRadius="lg" textAlign="center">
                <Text fontSize="xs" color="gray.500" fontWeight="medium">
                  Faturamento
                </Text>
                <Text fontSize="xl" fontWeight="bold" color="green.600">
                  {formatCurrencyCompact(totais.faturamento)}
                </Text>
              </Box>
              <Box p={4} bg="blue.50" borderRadius="lg" textAlign="center">
                <Text fontSize="xs" color="gray.500" fontWeight="medium">
                  Serviços
                </Text>
                <Text fontSize="xl" fontWeight="bold" color="blue.600">
                  {formatCurrencyCompact(totais.servicos)}
                </Text>
              </Box>
              <Box p={4} bg="purple.50" borderRadius="lg" textAlign="center">
                <Text fontSize="xs" color="gray.500" fontWeight="medium">
                  Vendas
                </Text>
                <Text fontSize="xl" fontWeight="bold" color="purple.600">
                  {formatCurrencyCompact(totais.saidas)}
                </Text>
              </Box>
            </SimpleGrid>

            {/* Gráfico de Faturamento x Impostos */}
            {chartDataFaturamento.length > 0 && (
              <Box bg="gray.50" p={4} borderRadius="lg">
                <HStack mb={4}>
                  <Icon as={TrendUp} boxSize={5} color="green.500" />
                  <Text fontSize="md" fontWeight="bold" color="gray.700">
                    Faturamento x Impostos
                  </Text>
                </HStack>
                <Box h="300px">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={chartDataFaturamento}
                      margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="mes" tick={{ fontSize: 11, fill: '#718096' }} />
                      <YAxis
                        tick={{ fontSize: 11, fill: '#718096' }}
                        tickFormatter={v => formatCurrencyCompact(v)}
                      />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Bar
                        dataKey="faturamento"
                        name="Faturamento"
                        fill="#48BB78"
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar
                        dataKey="impostos"
                        name="Impostos"
                        fill="#ED8936"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            )}
          </CardBody>
        </Card>

        {/* Seção Departamento Pessoal */}
        <Card overflow="hidden">
          <Box bg="blue.500" px={5} py={3}>
            <HStack>
              <Icon as={Users} boxSize={5} color="white" />
              <Text fontSize="lg" fontWeight="bold" color="white">
                Departamento Pessoal
              </Text>
            </HStack>
          </Box>
          <CardBody>
            {/* KPIs DP */}
            <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={4} mb={6}>
              <Box p={4} bg="purple.50" borderRadius="lg" textAlign="center">
                <Text fontSize="xs" color="gray.500" fontWeight="medium">
                  FGTS Total
                </Text>
                <Text fontSize="xl" fontWeight="bold" color="purple.600">
                  {formatCurrencyCompact(totaisFolha.fgts)}
                </Text>
              </Box>
              <Box p={4} bg="blue.50" borderRadius="lg" textAlign="center">
                <Text fontSize="xs" color="gray.500" fontWeight="medium">
                  DCTF-Web Total
                </Text>
                <Text fontSize="xl" fontWeight="bold" color="blue.600">
                  {formatCurrencyCompact(totaisFolha.dctfweb)}
                </Text>
              </Box>
              <Box p={4} bg="teal.50" borderRadius="lg" textAlign="center">
                <Text fontSize="xs" color="gray.500" fontWeight="medium">
                  Colaboradores
                </Text>
                <Text fontSize="xl" fontWeight="bold" color="teal.600">
                  {totaisFolha.vidas}
                </Text>
                {totaisFolha.prolabore > 0 && (
                  <Text fontSize="xs" color="gray.400">
                    + {totaisFolha.prolabore} pró-labore
                  </Text>
                )}
              </Box>
              <Box p={4} bg="yellow.50" borderRadius="lg" textAlign="center">
                <HStack justify="center" mb={1}>
                  <Icon as={Sun} boxSize={3} color="yellow.600" />
                  <Text fontSize="xs" color="gray.500" fontWeight="medium">
                    Férias
                  </Text>
                </HStack>
                <Text fontSize="xl" fontWeight="bold" color="yellow.600">
                  {totaisFolha.ferias}
                </Text>
              </Box>
              <Box p={4} bg="red.50" borderRadius="lg" textAlign="center">
                <HStack justify="center" mb={1}>
                  <Icon as={FirstAidKit} boxSize={3} color="red.500" />
                  <Text fontSize="xs" color="gray.500" fontWeight="medium">
                    Afastamentos
                  </Text>
                </HStack>
                <Text fontSize="xl" fontWeight="bold" color="red.500">
                  {totaisFolha.afastamentos}
                </Text>
              </Box>
              <Box p={4} bg="gray.50" borderRadius="lg" textAlign="center">
                <Text fontSize="xs" color="gray.500" fontWeight="medium">
                  Movimentação
                </Text>
                <HStack justify="center" spacing={3}>
                  <HStack spacing={1}>
                    <Icon as={UserPlus} boxSize={3} color="green.600" />
                    <Text fontSize="lg" fontWeight="bold" color="green.600">
                      {totaisFolha.admissoes}
                    </Text>
                  </HStack>
                  <HStack spacing={1}>
                    <Icon as={UserMinus} boxSize={3} color="red.600" />
                    <Text fontSize="lg" fontWeight="bold" color="red.600">
                      {totaisFolha.demissoes}
                    </Text>
                  </HStack>
                </HStack>
              </Box>
            </SimpleGrid>

            {/* Gráfico de FGTS e DCTF-Web */}
            {chartDataFolha.length > 0 && (
              <Box bg="gray.50" p={4} borderRadius="lg">
                <HStack mb={4}>
                  <Icon as={TrendUp} boxSize={5} color="blue.500" />
                  <Text fontSize="md" fontWeight="bold" color="gray.700">
                    Evolução FGTS e DCTF-Web
                  </Text>
                </HStack>
                <Box h="260px">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={chartDataFolha}
                      margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="mes" tick={{ fontSize: 11, fill: '#718096' }} />
                      <YAxis
                        tick={{ fontSize: 11, fill: '#718096' }}
                        tickFormatter={v => formatCurrencyCompact(v)}
                      />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Bar dataKey="fgts" name="FGTS" fill="#805AD5" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="dctfweb" name="DCTF-Web" fill="#3182CE" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            )}
          </CardBody>
        </Card>

        {/* Seção Notas Fiscais */}
        <Card overflow="hidden">
          <Box bg="teal.500" px={5} py={3}>
            <HStack>
              <Icon as={FileText} boxSize={5} color="white" />
              <Text fontSize="lg" fontWeight="bold" color="white">
                Notas Fiscais
              </Text>
            </HStack>
          </Box>
          <CardBody>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
              <Box p={4} bg="teal.50" borderRadius="lg" textAlign="center">
                <Text fontSize="xs" color="gray.500" fontWeight="medium">
                  Total de Notas
                </Text>
                <Text fontSize="2xl" fontWeight="bold" color="teal.600">
                  {totaisNotas.notas}
                </Text>
              </Box>
              <Box p={4} bg="red.50" borderRadius="lg" textAlign="center">
                <Text fontSize="xs" color="gray.500" fontWeight="medium">
                  Quebras de Sequência
                </Text>
                <Text fontSize="2xl" fontWeight="bold" color="red.600">
                  {totaisNotas.quebras}
                </Text>
              </Box>
              <Box p={4} bg="green.50" borderRadius="lg" textAlign="center">
                <Text fontSize="xs" color="gray.500" fontWeight="medium">
                  Taxa de Conformidade
                </Text>
                <Text fontSize="2xl" fontWeight="bold" color="green.600">
                  {totaisNotas.notas > 0
                    ? ((1 - totaisNotas.quebras / totaisNotas.notas) * 100).toFixed(1)
                    : 100}
                  %
                </Text>
              </Box>
              <Box p={4} bg="blue.50" borderRadius="lg" textAlign="center">
                <Text fontSize="xs" color="gray.500" fontWeight="medium">
                  Média Mensal
                </Text>
                <Text fontSize="2xl" fontWeight="bold" color="blue.600">
                  {Math.round(totaisNotas.notas / 12)}
                </Text>
              </Box>
            </SimpleGrid>
          </CardBody>
        </Card>

        {/* Sócios */}
        {empresa?.socios && empresa.socios.length > 0 && (
          <Card>
            <CardBody>
              <HStack mb={4}>
                <Icon as={Users} boxSize={5} color="purple.500" />
                <Text fontSize="lg" fontWeight="bold" color="gray.700">
                  Quadro Societário
                </Text>
              </HStack>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                {empresa.socios.map(socio => (
                  <Box
                    key={socio.inscricao}
                    p={4}
                    bg="gray.50"
                    borderRadius="lg"
                    border="1px"
                    borderColor="gray.200"
                  >
                    <Text fontWeight="600" color="gray.800" noOfLines={1}>
                      {socio.nome_socio}
                    </Text>
                    <Text fontSize="sm" color="gray.500">
                      {socio.inscricao}
                    </Text>
                    <HStack mt={2} justify="space-between">
                      <Badge colorScheme="purple">{socio.participacao}%</Badge>
                      <Text fontSize="xs" color="gray.400">
                        Desde{' '}
                        {socio.data_ingresso
                          ? new Date(socio.data_ingresso).toLocaleDateString('pt-BR')
                          : '-'}
                      </Text>
                    </HStack>
                    <Progress
                      value={socio.participacao}
                      size="sm"
                      colorScheme="purple"
                      borderRadius="full"
                      mt={2}
                    />
                  </Box>
                ))}
              </SimpleGrid>
            </CardBody>
          </Card>
        )}
      </VStack>
    </SecaoClienteLayout>
  );
};

export default SecaoClienteDossieEmpresa;
